/**
 * The name a freshly drawn contour arrives with.
 *
 * The old default was «Поле {дата}», and the owner ended up with four fields all called
 * «Поле 03.08.2026»: a default keyed on the day collides the moment two contours are drawn
 * in one sitting, which is the ordinary way this tool is used. A counter («Поле 3») would
 * not collide, but it would not tell them apart either — you would still have to click each
 * one to find out which is which.
 *
 * So the default is derived from the thing the field actually has: WHERE IT IS. Two
 * properties follow that a counter does not have:
 *
 *   * it is distinct per field in practice — 3 decimals is ~110 m, and two contours that
 *     close within 110 m of each other are the same field drawn twice;
 *   * it is a working coordinate. Paste it into the map's search box and it flies there
 *     (lib/coords.ts takes exactly this format), which no serial number does.
 *
 * NO GEOCODER. The nearest settlement would read better, and it was the first idea — but
 * fetching it means sending the centre of a contour somebody just drew to a third party,
 * automatically, on every draw. This product treats a field boundary as the private part
 * (see the 404-not-403 rule in api/deps.py), and the search box makes a point of never
 * putting a coordinate on the network. The user can type the village name himself, into a
 * box that is now focused and waiting; we do not do it behind his back.
 *
 * The server computes the same string for a create that arrives without a name
 * (api/fields.py `_default_name`), so the two do not disagree.
 */

import { bboxOf } from "./geo";
import type { GeoJsonPolygon } from "../api/types";

/** ~110 m. Short enough to read in a roster row, precise enough not to collide. */
const DECIMALS = 3;

export const defaultFieldName = (geometry: GeoJsonPolygon | null | undefined): string => {
  const box = bboxOf(geometry);
  if (box === null) return "";
  const [west, south, east, north] = box;
  const latitude = (south + north) / 2;
  const longitude = (west + east) / 2;
  return `${latitude.toFixed(DECIMALS)}, ${longitude.toFixed(DECIMALS)}`;
};
